import { FC, useState } from "react";

import { Box, Stack } from "@mui/material";
import { buildLocaleRecord, Locales } from "@simplyk/common";

import EasyDe from "../../../../public/images/internationalization/sticker/easy_de.svg";
import EasyEn from "../../../../public/images/internationalization/sticker/easy_en.svg";
import EasyFr from "../../../../public/images/internationalization/sticker/easy_fr.svg";

import { CloseDialogButton } from "./CloseDialogButton";
import { EmbedChoice } from "./enums";
import { ShareFormTabLayout } from "./ShareFormTabLayout";
import { ShareFormTabTitle } from "./ShareFormTabTitle";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ButtonTabs } from "@/components/ButtonTabs/ButtonTabs";
import Image from "@/components/Image/Image";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { ChevronLeft, ChevronRight } from "@/icons/outlined";

const gifSrcByTab = {
  ["Form"]: "/gifs/form-embed.gif",
  ["ButtonPopup"]: "/gifs/popup-embed.gif",
  ["Thermometer"]: "/gifs/thermometer.gif",
  ["Leaderboard"]: "/gifs/leaderboard.gif",
  ["LinkedButton"]: "/gifs/linked-button.gif",
};

const easyStickerByLocale = buildLocaleRecord({
  [Locales.EN]: <EasyEn />,
  [Locales.FR]: <EasyFr />,
  [Locales.ES]: <EasyEn />,
  [Locales.DE]: <EasyDe />,
});

type ChooseEmbedTypeTabProps = {
  availableEmbedChoices: EmbedChoice[];
  defaultChoice?: EmbedChoice;
  onClose?: () => void;
  onBackClick?: () => void;
  onChooseClick?: (choice: EmbedChoice) => void;
};

export const ChooseEmbedTypeTab: FC<ChooseEmbedTypeTabProps> = ({
  availableEmbedChoices,
  defaultChoice,
  onClose,
  onBackClick,
  onChooseClick,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();

  const [tab, setTab] = useState<EmbedChoice>(defaultChoice ?? (availableEmbedChoices[0] as EmbedChoice));

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: <ShareFormTabTitle>{t("common", "shareFormModal.embed.title")}</ShareFormTabTitle>,
        content: (
          <Stack gap={3}>
            <ButtonTabs
              justifyContent="center"
              activeTab={tab}
              onTabChange={(value) => setTab(value as EmbedChoice)}
              tabs={availableEmbedChoices.map((embedChoice) => ({
                value: embedChoice,
                label: t("common", `shareFormModal.embed.tab.${embedChoice}`),
                sticker: embedChoice === "LinkedButton" && easyStickerByLocale[locale],
              }))}
            />
            <Stack>
              {!isSmallScreen && (
                <DashboardInfobox
                  vibe="brand"
                  type="alert"
                  size="medium"
                  pointer="bottom"
                  hideIcon
                  title={t("common", `shareFormModal.embed.tab.${tab}.infoBox.title`)}
                >
                  {t("common", `shareFormModal.embed.tab.${tab}.infoBox.content`)}
                </DashboardInfobox>
              )}
              <Box sx={{ textAlign: "center" }}>
                <Image src={gifSrcByTab[tab]} alt="Form embed" width={isSmallScreen ? 260 : 450} height={undefined} />
              </Box>
              {isSmallScreen && (
                <DashboardInfobox
                  vibe="brand"
                  type="alert"
                  size="medium"
                  pointer="top"
                  hideIcon
                  title={t("common", `shareFormModal.embed.tab.${tab}.infoBox.title`)}
                >
                  {t("common", `shareFormModal.embed.tab.${tab}.infoBox.content`)}
                </DashboardInfobox>
              )}
            </Stack>
          </Stack>
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
            <DashboardButton
              vibe="brand"
              variant="filled"
              size="large"
              endIcon={<ChevronRight />}
              onClick={() => onChooseClick?.(tab)}
            >
              {t("common", "choose")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
